/*
 * Purpose: Mission Director cache — parallel enrichment results for interpret/plan
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { create } from 'zustand';
import type { MissionDirectorEnrichmentResult } from '@/services/mission-director/directorApi';
import { recordMissionDossier } from '@/services/mission-live/recordLiveAction';
import { useIntegrationDiscoveryStore } from '@/stores/integrationDiscoveryStore';
import { fetchMissionDirectorEnrichment } from '@/services/mission-director/directorApi';

interface MissionDirectorStore {
  missionSessionId: string;
  ragContextBlock: string;
  isEnriching: boolean;
  lastEnrichAt: number | null;
  lastElapsedMs: number | null;
  error: string | null;
  setMissionSession: (id: string) => void;
  applyEnrichment: (result: MissionDirectorEnrichmentResult) => void;
  clear: () => void;
}

export const useMissionDirectorStore = create<MissionDirectorStore>((set, get) => ({
  missionSessionId: '',
  ragContextBlock: '',
  isEnriching: false,
  lastEnrichAt: null,
  lastElapsedMs: null,
  error: null,

  setMissionSession: (id) => {
    set({
      missionSessionId: id,
      ragContextBlock: '',
      isEnriching: false,
      lastEnrichAt: null,
      lastElapsedMs: null,
      error: null,
    });
  },

  applyEnrichment: (result) => {
    if (!result.ok) {
      set({ error: result.error ?? 'Director enrichment failed', isEnriching: false });
      return;
    }

    const ragBlock = result.specialists?.rag?.contextBlock?.trim() ?? '';
    set({
      ragContextBlock: ragBlock,
      isEnriching: false,
      lastEnrichAt: Date.now(),
      lastElapsedMs: result.elapsedMs ?? null,
      error: null,
    });

    const discovery = result.specialists?.discovery;
    if (discovery) {
      recordMissionDossier({
        actor: 'ai',
        kind: 'discovery',
        summary: `Mission Director — ${discovery.proposals?.length ?? 0} integration proposal(s)`,
        reasoning: `Parallel specialists · ${result.elapsedMs ?? '?'}ms`,
        action: `RAG chunks: ${result.specialists?.rag?.chunkCount ?? 0} · search: ${discovery.searchProvider ?? 'none'}`,
        payload: {
          phase: result.phase,
          proposalIds: discovery.proposals?.map((p) => p.id) ?? [],
        },
      });
    }
  },

  clear: () => {
    set({
      ragContextBlock: '',
      isEnriching: false,
      lastEnrichAt: null,
      lastElapsedMs: null,
      error: null,
    });
  },
}));

export function startMissionDirectorEnrichment(input: {
  missionSessionId: string;
  outcome: string;
  interpretedGoal?: string;
  gaps?: string[];
  existingCatalog?: string;
  phase?: 'preamble' | 'enrich';
}): void {
  const { missionSessionId } = input;
  if (!missionSessionId) return;

  useMissionDirectorStore.setState({ missionSessionId, isEnriching: true, error: null });

  void fetchMissionDirectorEnrichment(input).then((result) => {
    useMissionDirectorStore.getState().applyEnrichment(result);
    if (result.specialists?.discovery?.proposals?.length) {
      useIntegrationDiscoveryStore
        .getState()
        .ingestDirectorProposals(missionSessionId, result.specialists.discovery.proposals);
    }
  });
}
